import React, { useState, useEffect } from 'react'
import { getFileTypeInfo } from '../utils/fileHelpers'

const CONFIG = {
  // ── Outer card ──
  cardBackground: 'var(--color-bg-card-solid)',
  cardBorderRadius: '20px',
  cardBorder: 'var(--border-thin)',
  cardMarginTop: '12px',
  cardMaxWidth: '360px',   // try 400-600px
  cardMinHeight: '140px',          // was 160 — slightly shorter overall

  // ── Preview area (now a portrait page shape) ──
  previewWidth: '90px',            // was '58%' — narrow fixed width like a doc page
  previewBackground: '#1a1a1a',
  previewBorderRadius: '10px',     // was 'var(--radius-lg)' — tighter
  previewMargin: '12px 0 12px 12px',  // a bit more breathing room

  // Letter fallback
  letterFontSize: '38px',          // was 48 — smaller to fit narrower preview
  letterFontWeight: 'var(--weight-bold)',
  letterColor: 'var(--color-text-primary)',

  // ── Text area ──
  textPadding: '16px 16px 16px 14px',
  textGap: '3px',                  // was 8 — tighter

  // Filename
  filenameFontSize: '15px',
  filenameFontWeight: 'var(--weight-regular)',
  filenameColor: 'var(--color-text-primary)',
  filenameLineHeight: 'var(--leading-snug)',

  // Label pill ("3 MB Words")
  labelFontSize: '12px',
  labelFontWeight: 'var(--weight-medium)',
  labelColor: 'var(--color-text-link)',
  labelBackground: '#3d3d3d',
  labelBorderRadius: '100px',      // was 'var(--radius-card)' — full capsule
  labelPadding: '2px 12px',        // was '3px 10px' — chunkier
}

/* ───────────────────────────────────────────────────────────────
 *  PERF — module-level PDF thumbnail cache
 *
 *  Rendering a PDF page to a canvas is EXPENSIVE — easily 100-500ms
 *  per PDF on a phone, and it blocks the main thread. Without a
 *  cache, every time DocumentCard mounts (initial load, tab switch,
 *  feed re-render) it re-renders the same PDF page from scratch.
 *
 *  This Map persists for the browser session keyed by URL. First
 *  render writes the dataURL; subsequent mounts use it directly.
 *  Failures are also cached so we don't keep retrying broken PDFs.
 *
 *  Could push to IndexedDB for cross-session persistence later —
 *  memory is fine for v2.
 * ─────────────────────────────────────────────────────────────── */
const pdfThumbCache = new Map()   // url -> { thumb?: string, failed?: boolean }
const pdfInflight = new Map()     // url -> Promise<dataURL | null>

/* ───────────────────────────────────────────────────────────────
 *  PdfThumbnail — renders page 1 of a PDF via PDF.js into a clean image
 *  Why: <iframe> previews look broken (browser PDF UI shows through, has
 *  scrollbars, alignment differs per browser). PDF.js renders to canvas,
 *  giving a clean image with no chrome.
 * ─────────────────────────────────────────────────────────────── */
function PdfThumbnail({ url }) {
  const [thumb, setThumb] = useState(() => pdfThumbCache.get(url)?.thumb || null)
  const [failed, setFailed] = useState(() => pdfThumbCache.get(url)?.failed || false)

  useEffect(() => {
    if (!url) return
    let cancelled = false

    // Cache hit — sync state and bail
    if (pdfThumbCache.has(url)) {
      const c = pdfThumbCache.get(url)
      setThumb(c.thumb || null)
      setFailed(c.failed || false)
      return
    }

    // Another instance already rendering this PDF? Wait for it.
    if (pdfInflight.has(url)) {
      pdfInflight.get(url).then(dataUrl => {
        if (cancelled) return
        if (dataUrl) setThumb(dataUrl)
        else setFailed(true)
      })
      return
    }

    const loadPdfJs = () => new Promise((resolve, reject) => {
      if (window.pdfjsLib) return resolve()
      const s = document.createElement('script')
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
      s.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
        resolve()
      }
      s.onerror = reject
      document.head.appendChild(s)
    })

    const render = async () => {
      try {
        await loadPdfJs()
        const pdf = await window.pdfjsLib.getDocument(url).promise
        const page = await pdf.getPage(1)
        const scale = 1.5
        const viewport = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
        return canvas.toDataURL('image/jpeg', 0.85)
      } catch (err) {
        console.error('PDF thumbnail render failed:', err)
        return null
      }
    }

    const promise = render().finally(() => {
      pdfInflight.delete(url)
    })

    pdfInflight.set(url, promise)

    promise.then(dataUrl => {
      if (dataUrl) {
        pdfThumbCache.set(url, { thumb: dataUrl })
        if (!cancelled) setThumb(dataUrl)
      } else {
        pdfThumbCache.set(url, { failed: true })
        if (!cancelled) setFailed(true)
      }
    })

    return () => { cancelled = true }
  }, [url])

  if (failed) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ad1212' }}>
        <span style={{ fontSize: CONFIG.letterFontSize, fontWeight: CONFIG.letterFontWeight, color: '#fff' }}>Pdf</span>
      </div>
    )
  }

  if (!thumb) {
    return <div style={{ width: '100%', height: '100%', background: CONFIG.previewBackground }} />
  }

  return (
    <img
      src={thumb}
      alt=""
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'top center',
        display: 'block',
      }}
    />
  )
}

/* ───────────────────────────────────────────────────────────────
 *  DocumentCard
 * ─────────────────────────────────────────────────────────────── */
function DocumentCard({ url, filename, mimeType, onOpen }) {
  const typeInfo = getFileTypeInfo(filename, mimeType)
  const ext = filename?.split('.').pop()?.toLowerCase()
  const isPdf = ext === 'pdf' || mimeType?.includes('pdf')
  const isImage = mimeType?.startsWith('image/')

  return (
    <div onClick={onOpen} style={{ background: CONFIG.cardBackground, borderRadius: CONFIG.cardBorderRadius, overflow: 'hidden', marginTop: CONFIG.cardMarginTop, cursor: 'pointer', border: CONFIG.cardBorder, display: 'flex', alignItems: 'stretch', minHeight: CONFIG.cardMinHeight, maxWidth: CONFIG.cardMaxWidth }}>
      <div style={{ width: CONFIG.previewWidth, flexShrink: 0, background: CONFIG.previewBackground, overflow: 'hidden', position: 'relative', borderRadius: CONFIG.previewBorderRadius, margin: CONFIG.previewMargin }}>
        {isImage ? (
          <img src={url} alt={filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : isPdf ? (
          <PdfThumbnail url={url} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: typeInfo.bg }}>
            <span style={{ fontSize: CONFIG.letterFontSize, fontWeight: CONFIG.letterFontWeight, color: CONFIG.letterColor }}>{typeInfo.letter}</span>
          </div>
        )}
      </div>
      <div style={{ flex: 1, padding: CONFIG.textPadding, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: CONFIG.textGap, overflow: 'hidden' }}>
        <p style={{ fontSize: CONFIG.filenameFontSize, fontWeight: CONFIG.filenameFontWeight, color: CONFIG.filenameColor, margin: 0, lineHeight: CONFIG.filenameLineHeight, wordBreak: 'break-word' }}>{filename}</p>
        <span style={{ fontSize: CONFIG.labelFontSize, color: CONFIG.labelColor, background: CONFIG.labelBackground, borderRadius: CONFIG.labelBorderRadius, padding: CONFIG.labelPadding, fontWeight: CONFIG.labelFontWeight, alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>{typeInfo.label}</span>
      </div>
    </div>
  )
}

/* PERF: React.memo with default shallow compare. Re-renders only
 * when url/filename/mimeType/onOpen change — all of which are
 * stable from MediaRenderer/HomePage above. */
export default React.memo(DocumentCard)